import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FaHome, FaUser, FaCamera, FaFileAlt, FaFolderOpen, FaTools,
  FaAward, FaEnvelope, FaSignOutAlt, FaExternalLinkAlt,
  FaShieldAlt, FaExclamationTriangle, FaInfoCircle, FaCheck,
  FaDownload, FaGraduationCap
} from 'react-icons/fa'
import { useAdmin } from '../../context/AdminContext'
import HeroEditor         from './components/HeroEditor'
import AboutEditor        from './components/AboutEditor'
import SkillsEditor       from './components/SkillsEditor'
import CertificatesEditor from './components/CertificatesEditor'
import EducationEditor    from './components/EducationEditor'
import ContactEditor      from './components/ContactEditor'
import ProfileImageEditor from './components/ProfileImageEditor'
import ResumeEditor       from './components/ResumeEditor'

const TABS = [
  { id: 'overview',     label: 'Overview',      icon: FaHome       },
  { id: 'profile',      label: 'Profile Photo', icon: FaCamera     },
  { id: 'resume',       label: 'Resume / CV',   icon: FaFileAlt    },
  { id: 'hero',         label: 'Hero',          icon: FaUser       },
  { id: 'about',        label: 'About',         icon: FaInfoCircle },
  { id: 'tools',        label: 'Skills',        icon: FaTools      },
  { id: 'certificates', label: 'Certificates',  icon: FaAward      },
  { id: 'educations',   label: 'Education',     icon: FaGraduationCap },
  { id: 'contact',      label: 'Contact',       icon: FaEnvelope   },
]

/* ── Overview ─────────────────────────────────────────────────── */
function OverviewPanel({ siteData, profileImage, resumeData, onReset, onNavigate }) {
  const [confirmReset, setConfirmReset] = useState(false)

  const stats = [
    { label:'Certificates', value: Array.isArray(siteData.certificates) ? siteData.certificates.length : 0, c:'#8b5cf6' },
    { label:'Educations',   value: Array.isArray(siteData.educations)   ? siteData.educations.length   : 0, c:'#f59e0b' },
    { label:'Skills',       value: Array.isArray(siteData.tools)        ? siteData.tools.length        : 0, c:'#06b6d4' },
    { label:'Job Status',   value: siteData.hero?.availableForWork ? 'Open 🟢' : 'Closed 🔴', c:'#10b981' },
  ]

  const guide = [
    { tab:'profile',      label:'Profile Photo', desc:'Upload your hero section circular photo.',          },
    { tab:'resume',       label:'Resume / CV',   desc:'Upload PDF resume — visitors can download it.',     },
    { tab:'hero',         label:'Hero',          desc:'Name, role, tagline, availability, stats.',         },
    { tab:'about',        label:'About',         desc:'Bio paragraphs and quick-fact cards.',              },
    { tab:'tools',        label:'Skills',        desc:'The tech stack badges in your skills section.',    },
    { tab:'certificates', label:'Certificates',  desc:'Course completions, degrees, or awards.',           },
    { tab:'educations',   label:'Education',     desc:'Academic background and degrees.',                  },
    { tab:'contact',      label:'Contact',       desc:'Email, phone, location, and call to action.',       },
  ]

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric' })
    : null

  return (
    <div className="space-y-5">

      {/* Welcome */}
      <div className="glass-card p-6"
        style={{ background:'linear-gradient(145deg,rgba(99,102,241,0.07),rgba(139,92,246,0.04))' }}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
            <FaShieldAlt size={15} className="text-white" />
          </div>
          <div>
            <h3 className="font-display font-bold text-white">Welcome back, Shiv 👋</h3>
            <p className="font-mono text-xs text-slate-600 mt-0.5">All changes save to browser automatically</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map(s => (
            <div key={s.label} className="card p-3 text-center"
              style={{ background:'rgba(0,0,0,0.3)' }}>
              <p className="font-display font-black text-xl" style={{ color:s.c }}>{s.value}</p>
              <p className="font-mono text-xs text-slate-600 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick status cards: Profile + Resume side by side */}
      <div className="grid sm:grid-cols-2 gap-4">

        {/* Profile photo status */}
        <div className="card p-4 flex items-center gap-4"
          style={{
            background: profileImage ? 'rgba(99,102,241,0.05)' : 'rgba(248,113,113,0.04)',
            borderColor: profileImage ? 'rgba(99,102,241,0.18)' : 'rgba(248,113,113,0.18)',
          }}>
          <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
            style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
            {profileImage
              ? <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
              : <span className="text-xl font-display font-black text-white">{siteData?.hero?.name?.charAt(0) || '?'}</span>
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-white text-xs">Profile Photo</p>
            <p className="text-xs font-mono mt-0.5" style={{ color: profileImage ? '#10b981' : '#f87171' }}>
              {profileImage ? '✅ Photo set' : '⚠️ Not uploaded'}
            </p>
          </div>
          <motion.button onClick={() => onNavigate('profile')}
            whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
            className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-mono text-white"
            style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
            {profileImage ? 'Change' : 'Upload'}
          </motion.button>
        </div>

        {/* Resume status */}
        <div className="card p-4 flex items-center gap-4"
          style={{
            background: resumeData ? 'rgba(239,68,68,0.04)' : 'rgba(248,113,113,0.04)',
            borderColor: resumeData ? 'rgba(239,68,68,0.18)' : 'rgba(248,113,113,0.18)',
          }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
            style={{ background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.2)' }}>
            {resumeData ? '📄' : '📭'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-white text-xs">Resume / CV</p>
            <p className="text-xs font-mono mt-0.5" style={{ color: resumeData ? '#10b981' : '#f87171' }}>
              {resumeData
                ? `✅ ${resumeData.fileName?.slice(0,22)}…`
                : '⚠️ No PDF uploaded'
              }
            </p>
            {resumeData?.uploadedAt && (
              <p className="text-xs font-mono text-slate-700">{formatDate(resumeData.uploadedAt)}</p>
            )}
          </div>
          <motion.button onClick={() => onNavigate('resume')}
            whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
            className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-mono text-white"
            style={{ background:'linear-gradient(135deg,#ef4444,#dc2626)' }}>
            {resumeData ? 'Replace' : 'Upload'}
          </motion.button>
        </div>
      </div>

      {/* Guide */}
      <div className="glass-card overflow-hidden"
        style={{ background:'rgba(10,10,18,0.6)' }}>
        <div className="px-5 py-3.5 border-b border-theme">
          <h3 className="font-display font-semibold text-white text-sm">Section Guide</h3>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {guide.map(({ tab, label, desc }) => (
            <motion.button key={tab} onClick={() => onNavigate(tab)}
              whileHover={{ x:4 }} whileTap={{ scale:0.99 }}
              className="w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-white/[0.03]">
              <div className="flex-1">
                <p className="font-display font-semibold text-sm text-white">{label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
              </div>
              <span className="text-slate-700 text-xs">→</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="rounded-2xl border border-red-500/10 p-5"
        style={{ background:'rgba(239,68,68,0.02)' }}>
        <h3 className="font-display font-semibold text-white text-sm mb-1 flex items-center gap-2">
          <FaExclamationTriangle size={11} className="text-red-400" /> Danger Zone
        </h3>
        <p className="font-mono text-xs text-slate-600 mb-3">Reset ALL content to defaults. Cannot be undone.</p>
        {confirmReset ? (
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-red-400">Sure?</span>
            <button onClick={() => { onReset(); setConfirmReset(false) }}
              className="px-3 py-1.5 text-xs rounded-lg font-mono text-red-400"
              style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.2)' }}>
              Yes, Reset
            </button>
            <button onClick={() => setConfirmReset(false)}
              className="px-3 py-1.5 text-xs rounded-lg font-mono text-slate-400"
              style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)' }}>
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmReset(true)}
            className="px-4 py-2 text-sm rounded-xl text-red-400"
            style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.12)' }}>
            Reset to Defaults
          </button>
        )}
      </div>
    </div>
  )
}

/* ── Dashboard root ───────────────────────────────────────────── */
export default function AdminDashboard({ onLogout }) {
  const {
    siteData,
    profileImage,
    resumeData,
    loading,
    logout,
    resetToDefaults
  } = useAdmin()
  const [activeTab, setActiveTab] = useState('overview')
  const [toast,     setToast]     = useState(null)

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2800) }
  const handleLogout = () => { logout(); onLogout() }

  return (
    <div className="min-h-screen text-slate-100" style={{ background:'#06060f' }}>

      {/* ── Top bar ── */}
      <header className="sticky top-0 z-40 border-b border-white/[0.06]"
        style={{ background:'rgba(6,6,15,0.92)', backdropFilter:'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center font-display font-black text-white text-sm"
              style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>S</div>
            <span className="font-display font-bold text-sm text-white">Admin Panel</span>
            <span className="hidden sm:block font-mono text-xs text-slate-700 border border-white/[0.06] px-2 py-0.5 rounded-md">
              SSP Portfolio
            </span>
          </div>
          <div className="flex items-center gap-1">
            <a href="/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 px-3 py-2 rounded-lg hover:bg-surface transition-all">
              <FaExternalLinkAlt size={9} /><span className="hidden sm:inline">View Site</span>
            </a>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/5 transition-all">
              <FaSignOutAlt size={11} /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-6">

          {/* ── Sidebar ── */}
          <aside className="hidden sm:flex flex-col w-48 flex-shrink-0">
            <nav className="space-y-0.5 sticky top-20">
              {TABS.map(({ id, label, icon: Icon }) => (
                <motion.button key={id} onClick={() => setActiveTab(id)}
                  whileHover={{ x:3 }} whileTap={{ scale:0.97 }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{
                    color:      activeTab===id ? '#fff' : '#6b7280',
                    background: activeTab===id ? 'rgba(99,102,241,0.12)' : 'transparent',
                    border:     activeTab===id ? '1px solid rgba(99,102,241,0.2)' : '1px solid transparent',
                  }}>
                  <Icon size={12} style={{ color: activeTab===id ? '#818cf8' : undefined }} />
                  <span className="flex-1 text-left text-xs">{label}</span>
                  {/* Status dots */}
                  {id==='profile' && (
                    <span className={`w-1.5 h-1.5 rounded-full ${profileImage ? 'bg-green-400' : 'bg-red-400'}`} />
                  )}
                  {id==='resume' && (
                    <span className={`w-1.5 h-1.5 rounded-full ${resumeData ? 'bg-green-400' : 'bg-red-400'}`} />
                  )}
                </motion.button>
              ))}
            </nav>
          </aside>

          {/* ── Mobile tabs ── */}
          <div className="sm:hidden w-full mb-4 -mx-4 px-4 overflow-x-auto">
            <div className="flex gap-1.5 pb-1 w-max">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs transition-all"
                  style={{
                    color:      activeTab===id ? '#818cf8' : '#6b7280',
                    background: activeTab===id ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                    border:     activeTab===id ? '1px solid rgba(99,102,241,0.2)' : '1px solid rgba(255,255,255,0.07)',
                  }}>
                  <Icon size={10}/>{label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Main ── */}
          <main className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab}
                initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-8 }} transition={{ duration:0.2, ease:[0.22,1,0.36,1] }}>
                {activeTab==='overview'     && <OverviewPanel siteData={siteData} profileImage={profileImage} resumeData={resumeData} onReset={() => { resetToDefaults(); showToast('Reset to defaults.') }} onNavigate={setActiveTab} />}
                {activeTab==='profile'      && <ProfileImageEditor onSaved={showToast} />}
                {activeTab==='resume'       && <ResumeEditor       onSaved={showToast} />}
                {activeTab==='hero'         && <HeroEditor         onSaved={showToast} />}
                {activeTab==='about'        && <AboutEditor        onSaved={showToast} />}
                {activeTab==='tools'        && <SkillsEditor       onSaved={showToast} />}
                {activeTab==='certificates' && <CertificatesEditor onSaved={showToast} />}
                {activeTab==='educations'   && <EducationEditor    onSaved={showToast} />}
                {activeTab==='contact'      && <ContactEditor      onSaved={showToast} />}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key={toast}
            initial={{ opacity:0, y:20, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:-10 }}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-2xl font-mono text-sm"
            style={{ background:'rgba(16,185,129,0.12)', border:'1px solid rgba(16,185,129,0.25)', color:'#34d399', backdropFilter:'blur(16px)' }}>
            <FaCheck size={11}/> {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
